/** The itinerary record's Usage tab (plan §5, §10.4, §9.5): everything that depends on the
 *  itinerary — what its delete guard counts — in three groups, each paged in place: Tours (how
 *  each depends on it: built on it, uses it as a template, cross-sells it; a deleted tour says
 *  so), Other itineraries (sharing its days) and Trips added (travellers' trips, newest first;
 *  above 1,000 counted only). Every row says "blocks Delete", as the activity record's tour
 *  rows do. Under the header, the live tours' upcoming departures and bookings from the used-by
 *  summary — asked for only once the panel has been on screen (D7), the band's query. */
import { Box, Link } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as itinerariesApi from '@/api/itineraries';
import type { ItineraryUsedByRef, ItineraryUsedByType } from '@/api/itineraries';
import { itineraryUsedBySummaryKey, useItineraryUsedBySummary } from '@/api/itineraryRecord';
import { USAGE_PAGE_SIZE, UsagePanel, UsageSummaryLine, type UsagePageQuery } from '@/features/catalog/usage/UsagePanel';
import { usageSummaryText } from '@/features/catalog/usage/usageModel';
import { useSeenOnce } from '@/features/records/page/useSeenOnce';

import { ITINERARY_USAGE_EMPTY, ITINERARY_USAGE_GROUPS, itineraryUsageFacts, itineraryUsageHref } from './itineraryUsageModel';

/** The totals: the used-by's first page (its `available_types`). */
export const itineraryUsageTypesKey = (id: number) => ['itineraries', 'usedBy', id, 'types'] as const;

/** One page of one group under a search ('' = none); under `['itineraries']`, so an itinerary
 *  write refreshes it. */
export function itineraryUsagePage(
  id: number,
  group: ItineraryUsedByType,
  page: number,
  search: string,
): UsagePageQuery<ItineraryUsedByRef> {
  return {
    queryKey: ['itineraries', 'usedBy', id, group, page, search],
    queryFn: () =>
      itinerariesApi.usedBy(id, { type: group, page, pageSize: USAGE_PAGE_SIZE, ...(search ? { search } : {}) }),
  };
}

const n = (v: number) => v.toLocaleString('en-US');

/** "6 live tours (3 use it as a template)", "500+ live tours". */
export function itineraryUsageTours(template: number | null) {
  return (count: number, capped: boolean): string => {
    const tours = `${n(count)}${capped ? '+' : ''} live ${count === 1 && !capped ? 'tour' : 'tours'}`;
    if (!template || capped) return tours;
    return `${tours} (${n(template)} ${template === 1 ? 'uses' : 'use'} it as a template)`;
  };
}

/** One record that depends on the itinerary: its name — a link to its console page (a trip has
 *  none: plain text, then its traveller) — and its facts. */
export function ItineraryUsageRow({ group, row }: { group: ItineraryUsedByType; row: ItineraryUsedByRef }) {
  const href = itineraryUsageHref(row);
  const name = row.description?.trim() || `#${row.id}`;
  const facts = itineraryUsageFacts(group, row);
  return (
    <Box component="li" sx={{ minWidth: 0, typography: 'body2', overflowWrap: 'anywhere' }} data-usage-row={`${group}:${row.id}`}>
      {href ? (
        <Link component={RouterLink} to={href} underline="hover" color="text.primary" sx={{ fontWeight: 600 }}>
          {name}
        </Link>
      ) : (
        name
      )}
      {group === 'demo_user_itinerary' && typeof row.user_id === 'number' && (
        <>
          {' · '}
          <Link component={RouterLink} to={`/users/${row.user_id}`} underline="hover">
            {`traveller #${row.user_id}`}
          </Link>
        </>
      )}
      {facts.length > 0 && (
        <Box component="span" sx={{ color: 'text.secondary' }}>
          {` · ${facts.join(' · ')}`}
        </Box>
      )}
    </Box>
  );
}

export function ItineraryUsageTab({ id }: { id: number }) {
  const qc = useQueryClient();
  const [panelRef, seen] = useSeenOnce<HTMLElement>();
  const typesQ = useQuery({
    queryKey: itineraryUsageTypesKey(id),
    queryFn: () => itinerariesApi.usedBy(id, { pageSize: 1 }),
  });
  const summaryQ = useItineraryUsedBySummary(id, { enabled: seen });
  const summary = summaryQ.data;

  const pageQuery = useCallback(
    (group: ItineraryUsedByType, page: number, search: string) => itineraryUsagePage(id, group, page, search),
    [id],
  );
  const renderRow = useCallback(
    (group: ItineraryUsedByType, row: ItineraryUsedByRef) => (
      <ItineraryUsageRow key={`${row.type_key}:${row.id}`} group={group} row={row} />
    ),
    [],
  );
  // Refresh: every used-by page (the totals and each group's) and the summary line.
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['itineraries', 'usedBy', id] });
    void qc.invalidateQueries({ queryKey: itineraryUsedBySummaryKey(id) });
  };

  return (
    <UsagePanel
      panelRef={panelRef}
      groups={ITINERARY_USAGE_GROUPS}
      types={typesQ.data?.available_types}
      typesFailed={typesQ.isError}
      onRefresh={refresh}
      pageQuery={pageQuery}
      renderRow={renderRow}
      empty={ITINERARY_USAGE_EMPTY}
      what="this itinerary"
      summary={
        <UsageSummaryLine
          text={
            summary
              ? usageSummaryText(summary, {
                  tours: itineraryUsageTours(summary.available ? summary.template_tours : null),
                  none: 'No live tour uses it as a template or is built on it',
                })
              : null
          }
          computedAt={summary?.computed_at}
          failed={summaryQ.isError}
          onRetry={() => void summaryQ.refetch()}
        />
      }
    />
  );
}
